import React, { useEffect, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { Block, BlockBetween, BlockDes, BlockHead, BlockHeadContent, BlockTitle, Button, Icon, PreviewCard } from "../../../components/Component";
import { Badge, Row, Col, Spinner } from "reactstrap";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";

const WorkflowDelayPredictionsPage = () => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [predictions, setPredictions] = useState([]);

  const loadPredictions = async () => {
    setLoading(true);
    try {
      const response = await api.getWorkflowDelayPredictions({ limit: 30 });
      setSummary(response?.summary || null);
      setPredictions(Array.isArray(response?.predictions) ? response.predictions : []);
    } catch (error) {
      console.error("Erreur chargement prédictions workflow:", error);
      toast.error("Impossible de charger la prédiction de retard workflow.");
      setSummary(null);
      setPredictions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPredictions();
  }, []);

  const getRiskBadge = (riskLevel) => {
    const map = {
      high: { color: "danger", label: "Élevé" },
      medium: { color: "warning", label: "Moyen" },
      low: { color: "success", label: "Faible" },
    };
    const value = map[riskLevel] || { color: "secondary", label: "Inconnu" };
    return <Badge color={value.color}>{value.label}</Badge>;
  };

  return (
    <React.Fragment>
      <Head title="Prédiction de retard workflow" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Prédiction de retard workflow</BlockTitle>
              <BlockDes className="text-soft">
                <p>Module dédié pour anticiper les retards et piloter les relances.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button color="light" onClick={loadPredictions} disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Actualisation...
                  </>
                ) : (
                  <>
                    <Icon name="reload" className="me-1" />
                    Actualiser
                  </>
                )}
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Row className="g-gs">
            <Col sm="6" lg="3">
              <PreviewCard>
                <div className="amount">{summary?.total ?? 0}</div>
                <div className="text-soft small">Actions suivies</div>
              </PreviewCard>
            </Col>
            <Col sm="6" lg="3">
              <PreviewCard>
                <div className="amount text-danger">{summary?.high_risk ?? 0}</div>
                <div className="text-soft small">Risque élevé</div>
              </PreviewCard>
            </Col>
            <Col sm="6" lg="3">
              <PreviewCard>
                <div className="amount text-warning">{summary?.medium_risk ?? 0}</div>
                <div className="text-soft small">Risque moyen</div>
              </PreviewCard>
            </Col>
            <Col sm="6" lg="3">
              <PreviewCard>
                <div className="amount text-info">{summary?.overdue ?? 0}</div>
                <div className="text-soft small">Déjà en retard</div>
              </PreviewCard>
            </Col>
          </Row>
        </Block>

        <Block>
          <PreviewCard>
            {loading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : predictions.length === 0 ? (
              <div className="text-center py-4">
                <span className="text-soft">Aucune observation à risque détectée.</span>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-ul mb-0">
                  <thead>
                    <tr>
                      <th>Observation</th>
                      <th>Risque retard</th>
                      <th>Relance cible</th>
                      <th>Relancer avant</th>
                      <th>Action recommandée</th>
                    </tr>
                  </thead>
                  <tbody>
                    {predictions.map((item) => (
                      <tr key={`delay-module-${item.observation_id}`}>
                        <td>
                          <div className="fw-medium">{item.title || `Observation #${item.observation_id}`}</div>
                          <div className="small text-soft">
                            {item.site || "Site non défini"}
                            {item.overdue_days > 0 ? ` · En retard ${item.overdue_days}j` : ""}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {getRiskBadge(item.risk_level)}
                            <span className="small">{Math.round((item.delay_probability || 0) * 100)}%</span>
                          </div>
                          {Array.isArray(item.reasons) && item.reasons.length > 0 && (
                            <div className="small text-soft mt-1">{item.reasons[0]}</div>
                          )}
                        </td>
                        <td className="small">{item.relance_target || "Responsable HSE"}</td>
                        <td className="small">
                          {item.relance_by ? new Date(item.relance_by).toLocaleString("fr-FR") : "-"}
                        </td>
                        <td className="small">{item.recommended_action || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PreviewCard>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default WorkflowDelayPredictionsPage;
